/**
 * Connections-side feature-flag wrappers.
 *
 * The canonical flag map lives in `@benchmark/core/feature-flags`
 * (shared with `apps/api`). These wrappers preserve the existing
 * per-flag call signatures so the 10+ existing call sites do not need
 * to change in lock-step with the system migration. Prefer the
 * `useFeatureFlag(name)` hook for new React call sites and
 * `useUserEmail()` for the underlying email projection.
 */

import { isFeatureEnabled, type AppEnv } from "@benchmark/core/feature-flags";
import { readFeatureOverride } from "./feature-flags-env";

const APP_ENV = import.meta.env.MODE as AppEnv;

/**
 * Whether to enforce inactivity-based auto-logout for the given user.
 * The rule (see `packages/core/src/feature-flags/flags.ts`) is
 * default-on in staging + production, off elsewhere. Honors the
 * `VITE_FEATURE_INACTIVITY_LOGOUT` env-var override (build-time).
 *
 * Pass the signed-in user's normalized email so the per-email rule
 * keys (if any are added later for staged rollout) take effect — call
 * `useUserEmail()` at the React boundary and forward the value.
 *
 * @agentic connections
 */
export const isInactivityLogoutEnabled = (
  userEmail: string | undefined,
): boolean =>
  isFeatureEnabled("inactivityLogout", {
    environment: APP_ENV,
    email: userEmail,
    override: readFeatureOverride("inactivityLogout"),
  });

/**
 * Boot-time variant of {@link isInactivityLogoutEnabled}. Runs inside
 * `initializeMedplumClient` before the React tree mounts and before any
 * profile is loaded, so the email cannot be projected. Calls
 * `isFeatureEnabled` with `email: undefined`, which means per-email
 * rules will not match — only the environments rule and the env-var
 * override are considered.
 *
 * @agentic connections
 */
export const isInactivityLogoutEnabledAtBoot = (): boolean =>
  isFeatureEnabled("inactivityLogout", {
    environment: APP_ENV,
    email: undefined,
    override: readFeatureOverride("inactivityLogout"),
  });
